import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PencilSimple, ArrowsClockwise, CheckCircle, UploadSimple, WarningCircle } from "@phosphor-icons/react";
import { fmtDateTime } from "@/components/common";

export const AMEND_FIELDS = [["consignee_name", "Consignee name"], ["consignee_address", "Consignee address"], ["consignee_phone", "Consignee phone"], ["destination", "Destination"], ["pieces_weight", "Pieces / weight"]];
const blank = () => ({ details: "", fields: {} });

// Client-side request dialog. `endpoint` differs between the client portal and the client-ops portal.
export const AwbAmendmentDialog = ({ open, onOpenChange, endpoint, onDone, shipmentNumber }) => {
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.details.trim() && !Object.values(form.fields).some((v) => v && v.trim())) return toast.error("Please describe what needs to change on the AWB");
    setBusy(true);
    try { await api.post(endpoint, form); toast.success("Amendment request sent — our team will issue a corrected AWB"); setForm(blank()); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send the amendment request"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="awb-amendment-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PencilSimple size={18} className="text-primary" /> Request AWB amendment{shipmentNumber ? ` — ${shipmentNumber}` : ""}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Tell us what is wrong on the airway bill. Fill in only the fields that need to change — the corrected values will be used to re-issue the AWB.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {AMEND_FIELDS.map(([k, label]) => (
              <div key={k} className={k === "consignee_address" ? "sm:col-span-2" : ""}>
                <Label className="text-xs uppercase tracking-wide">{label}</Label>
                <Input className="mt-1.5 rounded-sm" value={form.fields[k] || ""} onChange={(e) => setForm({ ...form, fields: { ...form.fields, [k]: e.target.value } })} placeholder="New value" data-testid={`awb-amend-${k}`} />
              </div>
            ))}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide">Amendment details</Label>
            <Textarea rows={3} className="mt-1.5 rounded-sm" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Describe the correction needed (e.g. consignee phone number is missing a digit)…" data-testid="awb-amend-details" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-sm" onClick={submit} disabled={busy} data-testid="awb-amend-submit"><PencilSimple size={16} className="mr-2" /> {busy ? "Sending…" : "Send amendment request"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const labels = Object.fromEntries(AMEND_FIELDS);

// Amendment history, shown on both portals. Team gets replace / resolve actions.
export const AwbAmendmentList = ({ shipment: s, isTeam, onChange }) => {
  const list = s.awb_amendments || [];
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  if (!list.length) return null;

  const replace = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !active) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f); fd.append("label", f.name); fd.append("amendment_id", active.id);
      fd.append("replace_doc_ids", (s.awb_documents || []).map((d) => d.id).join(","));
      const { data } = await api.post(`/shipments/${s.id}/awb-document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.extracted_awb ? `New AWB ${data.extracted_awb} issued — old AWB replaced, client asked to re-confirm` : "New AWB uploaded — old AWB replaced, client asked to re-confirm");
      setActive(null); onChange?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const resolve = async () => {
    setBusy(true);
    try { await api.post(`/shipments/${s.id}/awb-amendments/${active.id}/resolve`, { note }); toast.success("Amendment marked resolved"); setActive(null); setNote(""); onChange?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not resolve"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 space-y-2" data-testid="awb-amendments">
      <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">AWB amendment requests</p>
      {[...list].reverse().map((a) => { const open = a.status === "requested"; return (
        <div key={a.id} className={`rounded-sm border px-3 py-2 text-sm ${open ? "border-amber-300 bg-amber-50/60" : "border-border bg-muted/30"}`} data-testid={`awb-amendment-${a.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium flex items-center gap-1.5">{open ? <WarningCircle size={15} weight="fill" className="text-amber-600" /> : <CheckCircle size={15} weight="fill" className="text-emerald-600" />}{open ? "Amendment requested" : "Amended"}<span className="text-xs text-muted-foreground font-normal">· {a.requested_by} · {fmtDateTime(a.requested_at)}</span></p>
              {Object.entries(a.fields || {}).length > 0 && <ul className="mt-1 text-xs space-y-0.5">{Object.entries(a.fields).map(([k, v]) => <li key={k}><span className="text-muted-foreground">{labels[k] || k}:</span> <span className="font-medium">{v}</span></li>)}</ul>}
              {a.details && <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{a.details}</p>}
              {!open && <p className="mt-1 text-xs text-emerald-700" data-testid={`awb-amendment-resolution-${a.id}`}>Resolved by {a.resolved_by} · {fmtDateTime(a.resolved_at)}{a.new_awb_number ? ` · new AWB ${a.new_awb_number}` : ""}{a.old_awb_number && a.new_awb_number ? ` (was ${a.old_awb_number})` : ""}{a.resolution_note ? ` · ${a.resolution_note}` : ""}</p>}
            </div>
            {isTeam && open && <Button size="sm" variant="outline" className="rounded-sm h-8 shrink-0" onClick={() => setActive(a)} data-testid={`awb-amendment-action-${a.id}`}><ArrowsClockwise size={14} className="mr-1.5" /> Replace AWB</Button>}
          </div>
        </div>
      ); })}
      {isTeam && (
        <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
          <DialogContent className="max-w-md" data-testid="awb-replace-dialog">
            <DialogHeader><DialogTitle>Resolve AWB amendment</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">Upload the corrected airway bill. The current AWB document{(s.awb_documents || []).length > 1 ? "s" : ""} will be replaced, the AWB number re-read, and the client will be asked to confirm the new AWB.</p>
              <input ref={fileRef} type="file" className="hidden" onChange={replace} data-testid="awb-replace-input" />
              <Button className="w-full rounded-sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="awb-replace-upload"><UploadSimple size={16} className="mr-2" /> {busy ? "Uploading…" : "Upload corrected AWB"}</Button>
              <div className="pt-2 border-t border-border">
                <Label className="text-xs uppercase tracking-wide">Or resolve without a new document</Label>
                <Input className="mt-1.5 rounded-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to the client (e.g. AWB already correct)" data-testid="awb-resolve-note" />
                <Button variant="outline" className="mt-2 w-full rounded-sm" onClick={resolve} disabled={busy} data-testid="awb-resolve-btn"><CheckCircle size={16} className="mr-2" /> Mark resolved</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
